<template>
  <div>
    <section class="section">
      <div class="container">
        <h1 class="heading is-size-4">Privacy Policy</h1>
        <br />
        <p>
          This Privacy Policy describes how SpeakyReads (“we”, “us”, the "site",
          the "website") process personal data and other information through the
          SpeakyReads website, applications, and other online platforms (our
          “Services”). This Privacy Policy explains what types of information we
          collect, how we use it, our sharing practices, and your choices.
        </p>
        <br />
        <h2 class="title is-size-5">
          Who is responsible for managing my information?
        </h2>
        <p>
          SpeakyReads is responsible for the collection and proper management of
          any personal information you submit. We will keep your personal
          details secure and use the information you provide consistently with
          applicable privacy and data protection laws and the terms of this
          Privacy Policy.
        </p>
        <br />
        <h2 class="title is-size-5">
          Information We Collect
        </h2>
        <p>
          SpeakyReads collects information from you directly, automatically
          through its Services, and from third parties. Each collection
          mechanism is described in more detail in this Privacy Policy.
        </p>
        <br />
        <h2 class="title is-size-5">
          Information You Provide
        </h2>
        <p>
          When you register your account, we require you to sign in via an
          external login provider (Google or Facebook). During this process, you
          agree to share your email and names with us. In addition, when you
          subscribe to our Services or place an order with us, we collect
          payment details via a third party payment gateway (Stripe) and record
          your agreement to our
          <router-link to="/terms">Terms and Conditions</router-link>. We also
          collect audio recordings from your device during your language
          practice.
        </p>
        <br />
        <h2 class="title is-size-5">
          Automatically Collected Information
        </h2>
        <p>
          We automatically collect the following types of information when you
          use our Services:
        </p>
        <div class="content">
          <ul>
            <li>pages and files accessed;</li>
            <li>time of access;</li>
            <li>amount of data transferred;</li>
            <li>browser version;</li>
            <li>app version;</li>
            <li>operating system;</li>
            <li>language settings;</li>
            <li>device ID;</li>
            <li>
              previously visited pages (if you access our website via a link);
            </li>
            <li>IP address;</li>
            <li>session ID; and</li>
            <li>cookies, as explained in more detail below.</li>
          </ul>
        </div>
        <h2 class="title is-size-5">Cookies</h2>
        <p>
          When you visit our website, we store certain information from your
          browser using cookies or similar technologies such as local browser
          storage. A cookie is a small text file that is stored on your device
          through your browser. Cookies allow us to collect analytics data,
          remember your settings, authenticate you and perform other useful
          functions that help simplify your user experience.
        </p>
        <br />
        <h2 class="title is-size-5">How We Use Your Information</h2>
        <p>We use your information:</p>
        <div class="content">
          <ul>
            <li>
              to provide and improve our Services (including our speech
              recognition tools);
            </li>
            <li>
              in a pseudonymized, anonymized, or aggregated form to facilitate
              research in machine learning, artificial intelligence, and natural
              language processing;
            </li>
            <li>to process subscriptions to our Services;</li>
            <li>to protect our interests or the interests of third parties;</li>
            <li>to maintain and enhance the security of our Services;</li>
            <li>to prevent abuse of our Services;</li>
            <li>to comply with our legal obligations;</li>
            <li>to communicate with you;</li>
            <li>for analytics</li>
            <li>to perform or enforce our agreements;</li>
            <li>for any other purpose with your consent.</li>
          </ul>
        </div>
        <p>We use cookies:</p>
        <div class="content">
          <ul>
            <li>for login authentication and login status;</li>
            <li>
              for load distribution and corresponding optimization of our
              Services;
            </li>
            <li>to save your settings;</li>
            <li>to provide and improve our Services;</li>
            <li>to analyze user behavior on different devices</li>
            <li>for analytics.</li>
          </ul>
        </div>
        <br />
        <h2 class="title is-size-5">Sharing information with third parties</h2>
        <p>We disclose information:</p>
        <div class="content">
          <ul>
            <li>
              to contractors, service providers, and other third parties we use
              to support our Services, including payment processing service
              providers;
            </li>
            <li>to fulfill any purpose for which you provide it;</li>
            <li>
              for any other purpose disclosed to you when you provide the
              information; or
            </li>
            <li>with your consent.</li>
          </ul>
        </div>
        <p>We may also disclose your information:</p>
        <div class="content">
          <ul>
            <li>
              to comply with any court order, law, or legal process, including
              to respond to any government or regulatory request;
            </li>
            <li>
              to a buyer or other successor in the event of a merger,
              divestiture, restructuring, reorganization, dissolution, or other
              sale or transfer of some or all of our assets;
            </li>
            <li>
              if we believe disclosure is necessary or appropriate to protect
              our rights, property, or interests, our investors, or others.
            </li>
          </ul>
        </div>
        <br />
        <h2 class="title is-size-5">Bases for Processing</h2>
        <p>The legal bases for our processing activities include:</p>
        <div class="content">
          <ul>
            <li>
              Your consent (Article 6(1)(a) General Data Protection Regulation);
            </li>
            <li>
              Legitimate interests that are not overridden by your interests or
              your fundamental rights and freedoms (Article 6(1)(f) General Data
              Protection Regulation).
            </li>
          </ul>
        </div>
        <br />
        <h2 class="title is-size-5">Data Retention</h2>
        <p>
          We only store information for as long as is needed to fulfil our
          contractual or legal obligations or any other permissible purpose for
          which we have collected it, including to detect and prevent fraud or
          security incidents. When the applicable retention period has expired,
          we take measures to permanently delete the information, aggregate it,
          or anonymize it.
        </p>
        <br />
        <h2 class="title is-size-5">Security</h2>
        <p>
          We safeguard your information and protect it from unauthorized access.
          However, we cannot guarantee complete protection of your information
          since transmission via the internet is not 100% secure. If you have
          reason to believe that your interaction with us is no longer secure,
          please immediately notify us by contacting us.
        </p>
        <br />
        <h2 class="title is-size-5">Your rights</h2>
        <p>You have the right to request:</p>
        <div class="content">
          <ul>
            <li>information about how your personal data is processed</li>
            <li>a copy of that personal data</li>
            <li>
              that anything inaccurate in your personal data is corrected
              immediately
            </li>
          </ul>
        </div>
        <p>You can also:</p>
        <div class="content">
          <ul>
            <li>
              raise an objection about how your personal data is processed
            </li>
            <li>
              request that your personal data is erased if there is no longer a
              justification for it
            </li>
            <li>
              ask that the processing of your personal data is restricted in
              certain circumstances
            </li>
          </ul>
        </div>
        <p>
          You may receive notification emails from us. You can choose to opt out
          from these in the settings page.
        </p>
        <br />
        <h2 class="title is-size-5">Changes to This Privacy Policy</h2>
        <p>
          We encourage you to periodically review this Privacy Policy for the
          latest information on our privacy practices. We keep our privacy
          policy under regular review and we will place any updates on our
          webpage. This privacy policy was last updated on <b>08/02/2021.</b>
        </p>
        <br />
        <h2 class="title is-size-5">Contact Us</h2>
        <p>
          If you have any questions or comments regarding this Privacy Policy,
          our privacy practices, or if you wish to exercise your individual
          rights, please contact us at alex@speakyreads.com
        </p>
      </div>
    </section>
  </div>
</template>

<script>
export default {};
</script>
